import {
  AddressDisplay,
  Alert,
  Badge,
  Button,
  cn,
  FormField,
  Input,
  Modal,
  Select,
  Stepper,
  toast,
  Tooltip,
  truncateAddress,
} from "@gotts.ai/ui";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  concat,
  decodeFunctionResult,
  encodeFunctionData,
  encodePacked,
  getAddress,
  isAddress,
  keccak256,
  pad,
  parseUnits,
} from "viem";

import { AnimatedBalance } from "../components/AnimatedBalance";
import { CollapsibleSection } from "../components/CollapsibleSection";
import { PcbCard } from "../components/PcbCard";
import { ProtocolBadge } from "../components/ProtocolBadge";
import { TokenIcon } from "../components/TokenIcon";
import { useDeployment, useWallet } from "../context";
import { useRpcRequest } from "../hooks/useRpcRequest";
import { useTokenBalances } from "../hooks/useTokenBalances";
import { useTransactionFlow } from "../hooks/useTransactionFlow";
import { useTxFeedback } from "../hooks/useTxFeedback";
import {
  MOCK_ERC20_ABI,
  POOL_SWAP_TEST_ABI,
  V2_PAIR_ABI,
  V2_ROUTER_ABI,
  V3_FACTORY_ABI,
  V3_QUOTER_V2_ABI,
  V3_SWAP_ROUTER02_ABI,
  V3_SWAP_ROUTER_ABI,
  V4_QUOTER_ABI,
} from "../lib/abis";
import {
  deadline,
  DEVENV_TOKEN_PRICES,
  MAX_SQRT_PRICE_LIMIT,
  MAX_UINT256,
  MIN_SQRT_PRICE_LIMIT,
  sortTokens,
  TOKEN_DECIMALS,
  TOKEN_SYMBOLS,
  V3_FEE_TIERS,
  ZERO_ADDRESS,
} from "../lib/constants";
import {
  computeMinimumReceived,
  formatDisplayAmount,
  formatFeeWithPercent,
  formatSmartTime,
  formatSwapButtonLabel,
  formatTokenDisplay,
  formatUsd,
} from "../lib/format";

import type { StepDefinition } from "../hooks/useTransactionFlow";
import type { TokenSymbol } from "../lib/constants";
import type { Address, Hex } from "viem";

declare const __EXPLORER_URL__: string;

/** Tiny inline copy button (no external state needed) */
function InlineCopy({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 1500);
      }}
      className="inline-flex cursor-pointer items-center border-none bg-transparent text-white/40 transition-colors hover:text-white/70"
      title={copied ? "Copied!" : "Copy to clipboard"}
    >
      {copied ? (
        "\u2713"
      ) : (
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
    </button>
  );
}

/** Compute V2 pair address using CREATE2 */
function computePairAddress(
  factory: Address,
  tokenA: Address,
  tokenB: Address,
  initCodeHash: `0x${string}`,
): Address {
  const [token0, token1] =
    tokenA.toLowerCase() < tokenB.toLowerCase()
      ? [tokenA, tokenB]
      : [tokenB, tokenA];
  const salt = keccak256(
    encodePacked(["address", "address"], [token0, token1]),
  );
  const hash = keccak256(
    concat(["0xff", pad(factory, { size: 20 }), salt, initCodeHash]),
  );
  return getAddress("0x" + hash.slice(26));
}

const PROTOCOLS = ["V2", "V3", "V4"] as const;
type Protocol = (typeof PROTOCOLS)[number];

// feeOptions computed dynamically inside the component (see useMemo below)

// ---------------------------------------------------------------------------
// V4 seeded pool configs (matches devenv seed data)
// ---------------------------------------------------------------------------

interface V4PoolConfig {
  label: string;
  tokenA: TokenSymbol;
  tokenB: TokenSymbol;
  fee: number;
  tickSpacing: number;
}

const V4_POOLS: V4PoolConfig[] = [
  {
    label: "WETH/USDC (30 bps, Tick Spacing: 60)",
    tokenA: "WETH",
    tokenB: "USDC",
    fee: 3000,
    tickSpacing: 60,
  },
  {
    label: "WETH/DAI (30 bps, Tick Spacing: 60)",
    tokenA: "WETH",
    tokenB: "DAI",
    fee: 3000,
    tickSpacing: 60,
  },
];

// ---------------------------------------------------------------------------
// Slippage presets (bps = basis points, 100 bps = 1%)
// ---------------------------------------------------------------------------

const SLIPPAGE_PRESETS = [
  { label: "0.1%", bps: 10 },
  { label: "0.5%", bps: 50 },
  { label: "1.0%", bps: 100 },
] as const;

// ---------------------------------------------------------------------------
// Token pill selector
// ---------------------------------------------------------------------------

function TokenPill({
  symbol,
  onChange,
  testId,
  balances,
}: {
  symbol: TokenSymbol;
  onChange: (s: TokenSymbol) => void;
  testId: string;
  balances?: Record<string, { formatted: string }>;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, right: 0 });

  // Position the portal dropdown relative to the button
  useEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (btnRef.current?.contains(target) || menuRef.current?.contains(target))
        return;
      setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  return (
    <div className="shrink-0">
      <button
        ref={btnRef}
        type="button"
        onClick={() => {
          setOpen((v) => !v);
        }}
        className={cn(
          "octa-sm flex items-center gap-1.5 px-3 py-2 font-sans text-base font-normal text-white transition-colors",
          "bg-[#1e2430] hover:bg-[#2a3040]",
        )}
        data-testid={testId}
      >
        <TokenIcon symbol={symbol} size={24} />
        <span>{symbol}</span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          className="text-white/40"
        >
          <path
            d="M2 4 L5 7 L8 4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[9999] min-w-[160px] overflow-hidden rounded-sm border border-[var(--glass-border)] bg-[#1a1e26] p-1 shadow-lg"
            style={{ top: pos.top, right: pos.right }}
          >
            {TOKEN_SYMBOLS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  onChange(s);
                  setOpen(false);
                }}
                data-testid={`${testId}-option-${s}`}
                className={cn(
                  "flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left font-sans text-sm transition-colors",
                  s === symbol
                    ? "text-[var(--color-gold)]"
                    : "text-white/70 hover:bg-white/5 hover:text-white",
                )}
              >
                <TokenIcon symbol={s} size={20} />
                <span className="flex-1">{s}</span>
                {balances?.[s] && (
                  <span
                    className="font-mono text-[10px] text-white/30"
                    title={balances[s].formatted}
                  >
                    <AnimatedBalance
                      value={parseFloat(balances[s].formatted)}
                      format={(n) => formatDisplayAmount(n).display}
                      emDash={false}
                    />
                  </span>
                )}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SwapUI
// ---------------------------------------------------------------------------

export default function SwapUI() {
  const navigate = useNavigate();
  const { deployment } = useDeployment();
  const { walletClient, address, publicClient, mode, chain } = useWallet();
  const {
    balances,
    loading: balancesLoading,
    connectionError,
    refetch: refetchBalances,
  } = useTokenBalances();
  const flow = useTransactionFlow();
  const { feedbackClass, onTxSuccess, onTxError } = useTxFeedback();
  const { request: anvilRequest } = useRpcRequest();
  const [searchParams] = useSearchParams();

  // Original SwapRouter (with `deadline` in struct) on chains 1 & 31337;
  // SwapRouter02 (no `deadline`) on all other chains (Base, etc.)
  const useRouter02 = chain.id !== 1 && chain.id !== 31337;

  const [protocol, setProtocol] = useState<Protocol>("V3");
  const [tokenIn, setTokenIn] = useState<TokenSymbol>("WETH");
  const [tokenOut, setTokenOut] = useState<TokenSymbol>("USDC");
  const [amount, setAmount] = useState("");
  const [v3Fee, setV3Fee] = useState("3000");
  const [v4Pool, setV4Pool] = useState("0"); // index into V4_POOLS

  // Pre-fill from URL search params on initial mount
  const prefillApplied = useRef(false);
  useLayoutEffect(() => {
    if (prefillApplied.current) return;
    prefillApplied.current = true;

    const urlTokenIn = searchParams.get("tokenIn")?.toUpperCase();
    const urlTokenOut = searchParams.get("tokenOut")?.toUpperCase();
    const urlAmount = searchParams.get("amount");
    const urlProtocol = searchParams.get("protocol")?.toUpperCase();

    if (urlTokenIn && TOKEN_SYMBOLS.includes(urlTokenIn as TokenSymbol)) {
      setTokenIn(urlTokenIn as TokenSymbol);
    }
    if (urlTokenOut && TOKEN_SYMBOLS.includes(urlTokenOut as TokenSymbol)) {
      setTokenOut(urlTokenOut as TokenSymbol);
    }
    if (
      urlAmount &&
      !isNaN(parseFloat(urlAmount)) &&
      parseFloat(urlAmount) > 0
    ) {
      setAmount(urlAmount);
    }
    if (urlProtocol && PROTOCOLS.includes(urlProtocol as Protocol)) {
      setProtocol(urlProtocol as Protocol);
    }
  }, [searchParams]);

  // Slippage tolerance state
  const [slippageBps, setSlippageBps] = useState(50); // default 0.5%
  const [customSlippage, setCustomSlippage] = useState("");
  const [useCustomSlippage, setUseCustomSlippage] = useState(false);

  // UI panel state
  const [showSettings, setShowSettings] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [rateFlipped, setRateFlipped] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showFullPrecision, setShowFullPrecision] = useState(false);

  // Full precision amount ref (stores raw balance string from MAX)
  const fullPrecisionAmount = useRef("");

  // Quote auto-refresh
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [quoteStale, setQuoteStale] = useState(false);

  // V2 reserves for price impact calculation
  const [v2Reserves, setV2Reserves] = useState<{
    reserve0: bigint;
    reserve1: bigint;
  } | null>(null);

  // V3 on-chain quote
  const [v3Quote, setV3Quote] = useState<string | null>(null);
  const [v3QuoteLoading, setV3QuoteLoading] = useState(false);

  // V4 on-chain quote
  const [v4Quote, setV4Quote] = useState<string | null>(null);
  const [v4QuoteLoading, setV4QuoteLoading] = useState(false);

  // V3 fee tier auto-detection
  const [v3AvailableFees, setV3AvailableFees] = useState<number[]>([]);
  const [v3FeesLoaded, setV3FeesLoaded] = useState(false);

  // Button state: "idle" | "swapping" | "swapped"
  const [swapButtonState, setSwapButtonState] = useState<
    "idle" | "swapping" | "swapped"
  >("idle");

  // Token approval status
  const [approvalStatus, setApprovalStatus] = useState<
    "unknown" | "approved" | "needs-approval"
  >("unknown");

  // High impact confirmation checkbox
  const [highImpactAcknowledged, setHighImpactAcknowledged] = useState(false);

  // Last quote timestamp for refresh indicator
  const [lastQuoteTime, setLastQuoteTime] = useState<number | null>(null);
  const [quoteAge, setQuoteAge] = useState("");

  // Recent swaps history (persisted via localStorage)
  const [recentSwaps, setRecentSwaps] = useState<
    {
      tokenIn: string;
      tokenOut: string;
      amount: string;
      amountOut?: string;
      usdValue?: number;
      protocol: string;
      timestamp: number;
      hash?: string;
    }[]
  >(() => {
    try {
      const stored = localStorage.getItem("devenv-recent-swaps");
      if (!stored) return [];
      const parsed: unknown = JSON.parse(stored);
      return Array.isArray(parsed)
        ? (parsed as {
            tokenIn: string;
            tokenOut: string;
            amount: string;
            amountOut?: string;
            usdValue?: number;
            protocol: string;
            timestamp: number;
            hash?: string;
          }[])
        : [];
    } catch {
      return [];
    }
  });
  // showRecentSwaps handled by CollapsibleSection
  const [confirmClear, setConfirmClear] = useState(false);

  // Sync recent swaps to localStorage
  useEffect(() => {
    localStorage.setItem("devenv-recent-swaps", JSON.stringify(recentSwaps));
  }, [recentSwaps]);

  function getTokenAddress(symbol: TokenSymbol): Address | null {
    if (!deployment?.tokens) return null;
    const key = symbol.toLowerCase();
    const tokens = deployment.tokens as unknown as Record<string, Address>;
    return tokens[key] ?? null;
  }

  function getRouterAddress(): Address | null {
    if (!deployment) return null;
    let addr: string;
    if (protocol === "V2") addr = deployment.v2.router02;
    else if (protocol === "V3") addr = deployment.v3.router;
    else addr = deployment.v4.poolSwapTest;
    return isAddress(addr) ? addr : null;
  }

  const isConnected = mode !== "disconnected" && !!walletClient && !!address;

  // V4 pool options for dropdown
  const v4PoolOptions = V4_POOLS.map((p, i) => ({
    label: p.label,
    value: String(i),
  }));

  // Check if the selected token pair matches any V4 pool
  const selectedV4Pool = (V4_POOLS[parseInt(v4Pool)] ??
    V4_POOLS[0]) as V4PoolConfig;
  const v4PairHasPool = useMemo(() => {
    if (protocol !== "V4") return true;
    return V4_POOLS.some(
      (p) =>
        (p.tokenA === tokenIn && p.tokenB === tokenOut) ||
        (p.tokenB === tokenIn && p.tokenA === tokenOut),
    );
  }, [protocol, tokenIn, tokenOut]);

  // Find the matching V4 pool config for the current token pair
  const matchingV4Pool = useMemo(() => {
    return V4_POOLS.find(
      (p) =>
        (p.tokenA === tokenIn && p.tokenB === tokenOut) ||
        (p.tokenB === tokenIn && p.tokenA === tokenOut),
    );
  }, [tokenIn, tokenOut]);

  // Effective slippage in basis points
  const effectiveSlippageBps = useCustomSlippage
    ? Math.max(
        0,
        Math.min(5000, Math.round(parseFloat(customSlippage || "0") * 100)),
      )
    : slippageBps;

  // Dynamic V3 fee tier options — marks unavailable tiers
  const feeOptions = useMemo(() => {
    return V3_FEE_TIERS.map((f) => ({
      label:
        v3AvailableFees.length > 0 && !v3AvailableFees.includes(f.value)
          ? `${f.label} (no pool)`
          : f.label,
      value: String(f.value),
    }));
  }, [v3AvailableFees]);

  // Whether the selected V3 fee tier has a pool
  const v3PairHasPool = useMemo(() => {
    if (protocol !== "V3") return true;
    // If we haven't finished querying, assume valid (don't block UI while loading)
    if (!v3FeesLoaded) return true;
    if (v3AvailableFees.length === 0) return false;
    return v3AvailableFees.includes(parseInt(v3Fee));
  }, [protocol, v3FeesLoaded, v3AvailableFees, v3Fee]);

  // Fetch V2 reserves when protocol is V2 and token pair changes
  useEffect(() => {
    if (
      protocol !== "V2" ||
      !deployment?.v2 ||
      !isAddress(deployment.v2.factory)
    ) {
      setV2Reserves(null);
      return;
    }
    const tokenInAddr = getTokenAddress(tokenIn);
    const tokenOutAddr = getTokenAddress(tokenOut);
    if (!tokenInAddr || !tokenOutAddr || tokenInAddr === tokenOutAddr) {
      setV2Reserves(null);
      return;
    }
    const pairAddress = computePairAddress(
      deployment.v2.factory,
      tokenInAddr,
      tokenOutAddr,
      deployment.v2.initCodePairHash,
    );
    const callData = encodeFunctionData({
      abi: V2_PAIR_ABI,
      functionName: "getReserves",
    });
    anvilRequest("eth_call", [{ to: pairAddress, data: callData }, "latest"])
      .then((result) => {
        const strResult = result as string;
        if (!strResult || strResult === "0x") {
          setV2Reserves(null);
          return;
        }
        const decoded = decodeFunctionResult({
          abi: V2_PAIR_ABI,
          functionName: "getReserves",
          data: strResult as `0x${string}`,
        });
        setV2Reserves({
          reserve0: decoded[0],
          reserve1: decoded[1],
        });
        setLastQuoteTime(Date.now());
      })
      .catch(() => {
        setV2Reserves(null);
      });
  }, [protocol, tokenIn, tokenOut, deployment, refreshTrigger]);

  // Auto-detect available V3 fee tiers for the selected token pair
  useEffect(() => {
    if (
      protocol !== "V3" ||
      !deployment?.v3.factory ||
      !isAddress(deployment.v3.factory)
    ) {
      setV3AvailableFees([]);
      setV3FeesLoaded(false);
      return;
    }
    const tokenInAddr = getTokenAddress(tokenIn);
    const tokenOutAddr = getTokenAddress(tokenOut);
    if (!tokenInAddr || !tokenOutAddr || tokenInAddr === tokenOutAddr) {
      setV3AvailableFees([]);
      setV3FeesLoaded(false);
      return;
    }

    setV3FeesLoaded(false);
    let cancelled = false;
    void Promise.allSettled(
      V3_FEE_TIERS.map(async (tier) => {
        const callData = encodeFunctionData({
          abi: V3_FACTORY_ABI,
          functionName: "getPool",
          args: [tokenInAddr, tokenOutAddr, tier.value],
        });
        const result = (await anvilRequest("eth_call", [
          { to: deployment.v3.factory, data: callData },
          "latest",
        ])) as string;
        if (!result || result === "0x") return null;
        const decoded = decodeFunctionResult({
          abi: V3_FACTORY_ABI,
          functionName: "getPool",
          data: result as `0x${string}`,
        });
        const poolAddr = decoded as unknown as string;
        if (!poolAddr || poolAddr === ZERO_ADDRESS) return null;
        return tier.value;
      }),
    ).then((results) => {
      if (cancelled) return;
      const available = results
        .map((r): number | null =>
          r.status === "fulfilled" ? (r.value as number | null) : null,
        )
        .filter((v): v is number => v !== null);
      setV3AvailableFees(available);
      setV3FeesLoaded(true);
      // Auto-select the first available fee tier if current selection has no pool
      if (available.length > 0 && !available.includes(parseInt(v3Fee))) {
        setV3Fee(String(available[0]));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [protocol, tokenIn, tokenOut, deployment]);

  // Fetch V3 quote via QuoterV2 eth_call
  useEffect(() => {
    if (
      protocol !== "V3" ||
      !deployment?.v3.quoterV2 ||
      !isAddress(deployment.v3.quoterV2) ||
      !v3FeesLoaded ||
      !amount ||
      parseFloat(amount) <= 0
    ) {
      setV3Quote(null);
      setV3QuoteLoading(false);
      return;
    }
    const tokenInAddr = getTokenAddress(tokenIn);
    const tokenOutAddr = getTokenAddress(tokenOut);
    if (!tokenInAddr || !tokenOutAddr || tokenInAddr === tokenOutAddr) {
      setV3Quote(null);
      return;
    }

    const decimalsIn = TOKEN_DECIMALS[tokenIn] ?? 18;
    let amountIn: bigint;
    try {
      amountIn = parseUnits(amount, decimalsIn);
    } catch {
      setV3Quote(null);
      return;
    }
    if (amountIn === 0n) {
      setV3Quote(null);
      return;
    }

    setV3QuoteLoading(true);
    const callData = encodeFunctionData({
      abi: V3_QUOTER_V2_ABI,
      functionName: "quoteExactInputSingle",
      args: [
        {
          tokenIn: tokenInAddr,
          tokenOut: tokenOutAddr,
          amountIn,
          fee: parseInt(v3Fee),
          sqrtPriceLimitX96: 0n,
        },
      ],
    });

    anvilRequest("eth_call", [
      { to: deployment.v3.quoterV2, data: callData },
      "latest",
    ])
      .then((result) => {
        const strResult = result as string;
        if (!strResult || strResult === "0x") {
          setV3Quote(null);
          setV3QuoteLoading(false);
          return;
        }
        const decoded = decodeFunctionResult({
          abi: V3_QUOTER_V2_ABI,
          functionName: "quoteExactInputSingle",
          data: strResult as `0x${string}`,
        });
        const amountOut = decoded[0];
        const decimalsOut = TOKEN_DECIMALS[tokenOut] ?? 18;
        const divisor = 10n ** BigInt(decimalsOut);
        const whole = amountOut / divisor;
        const frac = amountOut % divisor;
        const fracStr = frac
          .toString()
          .padStart(decimalsOut, "0")
          .slice(0, 6)
          .replace(/0+$/, "");
        setV3Quote(fracStr ? `${String(whole)}.${fracStr}` : String(whole));
        setV3QuoteLoading(false);
        setLastQuoteTime(Date.now());
      })
      .catch(() => {
        setV3Quote(null);
        setV3QuoteLoading(false);
      });
  }, [
    protocol,
    tokenIn,
    tokenOut,
    amount,
    v3Fee,
    v3FeesLoaded,
    deployment,
    refreshTrigger,
  ]);

  // Fetch V4 quote via V4Quoter eth_call
  useEffect(() => {
    if (
      protocol !== "V4" ||
      !deployment?.v4.quoter ||
      !isAddress(deployment.v4.quoter) ||
      !amount ||
      parseFloat(amount) <= 0
    ) {
      setV4Quote(null);
      setV4QuoteLoading(false);
      return;
    }
    const poolConfig = matchingV4Pool;
    if (!poolConfig) {
      setV4Quote(null);
      setV4QuoteLoading(false);
      return;
    }
    const tokenInAddr = getTokenAddress(tokenIn);
    const tokenOutAddr = getTokenAddress(tokenOut);
    if (!tokenInAddr || !tokenOutAddr || tokenInAddr === tokenOutAddr) {
      setV4Quote(null);
      return;
    }

    const decimalsIn = TOKEN_DECIMALS[tokenIn] ?? 18;
    let amountIn: bigint;
    try {
      amountIn = parseUnits(amount, decimalsIn);
    } catch {
      setV4Quote(null);
      return;
    }
    if (amountIn === 0n) {
      setV4Quote(null);
      return;
    }

    const [currency0, currency1] = sortTokens(tokenInAddr, tokenOutAddr);
    const zeroForOne = tokenInAddr.toLowerCase() === currency0.toLowerCase();

    setV4QuoteLoading(true);
    const callData = encodeFunctionData({
      abi: V4_QUOTER_ABI,
      functionName: "quoteExactInputSingle",
      args: [
        {
          poolKey: {
            currency0,
            currency1,
            fee: poolConfig.fee,
            tickSpacing: poolConfig.tickSpacing,
            hooks: ZERO_ADDRESS,
          },
          zeroForOne,
          exactAmount: amountIn,
          hookData: "0x" as Hex,
        },
      ],
    });

    // V4 Quoter uses poolManager.unlock() callback + revert-to-return pattern,
    // which requires state writes (transient storage). Explicit gas avoids
    // edge-case default limits; `from` avoids zero-address caller issues.
    anvilRequest("eth_call", [
      {
        to: deployment.v4.quoter,
        data: callData,
        from: address ?? deployment.deployer,
        gas: "0x1C9C380", // 30M
      },
      "latest",
    ])
      .then((result) => {
        const strResult = result as string;
        if (!strResult || strResult === "0x") {
          setV4Quote(null);
          setV4QuoteLoading(false);
          return;
        }
        const decoded = decodeFunctionResult({
          abi: V4_QUOTER_ABI,
          functionName: "quoteExactInputSingle",
          data: strResult as `0x${string}`,
        });
        const amountOut = decoded[0];
        const decimalsOut = TOKEN_DECIMALS[tokenOut] ?? 18;
        const divisor = 10n ** BigInt(decimalsOut);
        const whole = amountOut / divisor;
        const frac = amountOut % divisor;
        const fracStr = frac
          .toString()
          .padStart(decimalsOut, "0")
          .slice(0, 6)
          .replace(/0+$/, "");
        setV4Quote(fracStr ? `${String(whole)}.${fracStr}` : String(whole));
        setV4QuoteLoading(false);
        setLastQuoteTime(Date.now());
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.warn("[V4 Quote] eth_call failed:", err);
        setV4Quote(null);
        setV4QuoteLoading(false);
      });
  }, [
    protocol,
    tokenIn,
    tokenOut,
    amount,
    deployment,
    matchingV4Pool,
    refreshTrigger,
    address,
  ]);

  // Price impact: computed for V2 from reserves, N/A for V3/V4
  const priceImpactLabel = useMemo(() => {
    if (protocol !== "V2" || !v2Reserves || !amount) return "N/A";
    const tokenInAddr = getTokenAddress(tokenIn);
    const tokenOutAddr = getTokenAddress(tokenOut);
    if (!tokenInAddr || !tokenOutAddr) return "N/A";

    const decimals = TOKEN_DECIMALS[tokenIn] ?? 18;
    let amountIn: bigint;
    try {
      amountIn = parseUnits(amount, decimals);
    } catch {
      return "N/A";
    }
    if (amountIn === 0n) return "N/A";

    // Determine which reserve corresponds to tokenIn
    const isToken0 = tokenInAddr.toLowerCase() < tokenOutAddr.toLowerCase();
    const reserveIn = isToken0 ? v2Reserves.reserve0 : v2Reserves.reserve1;
    if (reserveIn === 0n) return "N/A";

    // Price impact = amountIn / (reserveIn + amountIn) * 100
    const impact = (Number(amountIn) / Number(reserveIn + amountIn)) * 100;

    return impact;
  }, [protocol, v2Reserves, amount, tokenIn, tokenOut, deployment]);

  const tokenInBalance = balances[tokenIn];
  const tokenOutBalance = balances[tokenOut];

  // Estimated output amount (V2 only — V3/V4 require on-chain quoting)
  const estimatedOutput = useMemo(() => {
    if (protocol !== "V2" || !v2Reserves || !amount) return null;
    const tokenInAddr = getTokenAddress(tokenIn);
    const tokenOutAddr = getTokenAddress(tokenOut);
    if (!tokenInAddr || !tokenOutAddr) return null;

    const decimalsIn = TOKEN_DECIMALS[tokenIn] ?? 18;
    let amountIn: bigint;
    try {
      amountIn = parseUnits(amount, decimalsIn);
    } catch {
      return null;
    }
    if (amountIn === 0n) return null;

    // Determine which reserve corresponds to tokenIn
    const isToken0 = tokenInAddr.toLowerCase() < tokenOutAddr.toLowerCase();
    const reserveIn = isToken0 ? v2Reserves.reserve0 : v2Reserves.reserve1;
    const reserveOut = isToken0 ? v2Reserves.reserve1 : v2Reserves.reserve0;
    if (reserveIn === 0n || reserveOut === 0n) return null;

    // Standard Uniswap V2 getAmountOut formula:
    // amountOut = (amountIn * 997 * reserveOut) / (reserveIn * 1000 + amountIn * 997)
    const amountInWithFee = amountIn * 997n;
    const numerator = amountInWithFee * reserveOut;
    const denominator = reserveIn * 1000n + amountInWithFee;
    const amountOut = numerator / denominator;

    const decimalsOut = TOKEN_DECIMALS[tokenOut] ?? 18;
    // Format to human-readable string
    const divisor = 10n ** BigInt(decimalsOut);
    const whole = amountOut / divisor;
    const frac = amountOut % divisor;
    const fracStr = frac
      .toString()
      .padStart(decimalsOut, "0")
      .slice(0, 6)
      .replace(/0+$/, "");
    return fracStr ? `${String(whole)}.${fracStr}` : String(whole);
  }, [protocol, v2Reserves, amount, tokenIn, tokenOut, deployment]);

  // Pre-flight balance validation
  const insufficientBalance = useMemo(() => {
    if (!amount || !tokenInBalance) return false;
    try {
      const decimals = TOKEN_DECIMALS[tokenIn] ?? 18;
      return parseUnits(amount, decimals) > tokenInBalance.raw;
    } catch {
      return false; // invalid number, let other validation catch it
    }
  }, [amount, tokenIn, tokenInBalance]);

  // -------------------------------------------------------------------------
  // Raw calldata generation
  // -------------------------------------------------------------------------

  const swapCalldata = useMemo((): Hex | null => {
    if (!deployment || !amount || tokenIn === tokenOut) return null;
    const tokenInAddr = getTokenAddress(tokenIn);
    const tokenOutAddr = getTokenAddress(tokenOut);
    if (!tokenInAddr || !tokenOutAddr) return null;

    const decimals = TOKEN_DECIMALS[tokenIn] ?? 18;
    const rawAmount = fullPrecisionAmount.current || amount;
    let amountIn: bigint;
    try {
      amountIn = parseUnits(rawAmount, decimals);
    } catch {
      return null;
    }
    if (amountIn === 0n) return null;

    try {
      if (protocol === "V2") {
        return encodeFunctionData({
          abi: V2_ROUTER_ABI,
          functionName: "swapExactTokensForTokens",
          args: [
            amountIn,
            0n,
            [tokenInAddr, tokenOutAddr],
            address ?? ZERO_ADDRESS,
            deadline(),
          ],
        });
      } else if (protocol === "V3") {
        const [token0] = sortTokens(tokenInAddr, tokenOutAddr);
        const zeroForOne = tokenInAddr.toLowerCase() === token0.toLowerCase();
        const sqrtPriceLimitX96 = zeroForOne
          ? MIN_SQRT_PRICE_LIMIT
          : MAX_SQRT_PRICE_LIMIT;
        if (useRouter02) {
          return encodeFunctionData({
            abi: V3_SWAP_ROUTER02_ABI,
            functionName: "exactInputSingle",
            args: [
              {
                tokenIn: tokenInAddr,
                tokenOut: tokenOutAddr,
                fee: parseInt(v3Fee),
                recipient: address ?? ZERO_ADDRESS,
                amountIn,
                amountOutMinimum: 0n,
                sqrtPriceLimitX96,
              },
            ],
          });
        }
        return encodeFunctionData({
          abi: V3_SWAP_ROUTER_ABI,
          functionName: "exactInputSingle",
          args: [
            {
              tokenIn: tokenInAddr,
              tokenOut: tokenOutAddr,
              fee: parseInt(v3Fee),
              recipient: address ?? ZERO_ADDRESS,
              deadline: deadline(),
              amountIn,
              amountOutMinimum: 0n,
              sqrtPriceLimitX96,
            },
          ],
        });
      } else {
        const poolConfig = matchingV4Pool ?? selectedV4Pool;
        const [currency0, currency1] = sortTokens(tokenInAddr, tokenOutAddr);
        const zeroForOne =
          tokenInAddr.toLowerCase() === currency0.toLowerCase();
        const sqrtPriceLimitX96 = zeroForOne
          ? MIN_SQRT_PRICE_LIMIT
          : MAX_SQRT_PRICE_LIMIT;
        return encodeFunctionData({
          abi: POOL_SWAP_TEST_ABI,
          functionName: "swap",
          args: [
            {
              currency0,
              currency1,
              fee: poolConfig.fee,
              tickSpacing: poolConfig.tickSpacing,
              hooks: ZERO_ADDRESS,
            },
            {
              zeroForOne,
              amountSpecified: -amountIn,
              sqrtPriceLimitX96,
            },
            { takeClaims: false, settleUsingBurn: false },
            "0x" as Hex,
          ],
        });
      }
    } catch {
      return null;
    }
  }, [
    protocol,
    tokenIn,
    tokenOut,
    amount,
    v3Fee,
    matchingV4Pool,
    selectedV4Pool,
    deployment,
    address,
    useRouter02,
  ]);

  // -------------------------------------------------------------------------
  // Gas estimate
  // -------------------------------------------------------------------------

  const [gasEstimate, setGasEstimate] = useState<string | null>(null);
  const [gasPrice, setGasPrice] = useState<bigint | null>(null);

  const getRouterForEstimate = useCallback((): Address | null => {
    if (!deployment) return null;
    let addr: string;
    if (protocol === "V2") addr = deployment.v2.router02;
    else if (protocol === "V3") addr = deployment.v3.router;
    else addr = deployment.v4.poolSwapTest;
    return isAddress(addr) ? addr : null;
  }, [deployment, protocol]);

  useEffect(() => {
    if (!swapCalldata) {
      setGasEstimate(null);
      setGasPrice(null);
      return;
    }
    const router = getRouterForEstimate();
    if (!router || !address) {
      setGasEstimate(null);
      setGasPrice(null);
      return;
    }

    Promise.all([
      anvilRequest("eth_estimateGas", [
        { from: address, to: router, data: swapCalldata },
      ]),
      anvilRequest("eth_gasPrice", []),
    ])
      .then(([rawGas, rawPrice]) => {
        const gasResult = rawGas as string;
        const priceResult = rawPrice as string;
        if (gasResult) {
          setGasEstimate(BigInt(gasResult).toLocaleString());
          setGasPrice(priceResult ? BigInt(priceResult) : null);
        } else {
          setGasEstimate(null);
          setGasPrice(null);
        }
      })
      .catch(() => {
        setGasEstimate("estimate failed");
        setGasPrice(null);
      });
  }, [swapCalldata, address, getRouterForEstimate, anvilRequest]);

  const gasCost = useMemo(() => {
    if (!gasEstimate || !gasPrice || gasEstimate === "estimate failed")
      return null;
    const gasUnits = BigInt(gasEstimate.replace(/,/g, ""));
    const costWei = gasUnits * gasPrice;
    const ethCost = Number(costWei) / 1e18;
    const usdCost = ethCost * (DEVENV_TOKEN_PRICES.WETH ?? 0);
    return { ethCost, usdCost };
  }, [gasEstimate, gasPrice]);

  // -------------------------------------------------------------------------
  // Quote age timer
  // -------------------------------------------------------------------------

  const refreshQuote = useCallback(() => {
    setRefreshTrigger((c) => c + 1);
  }, []);

  useEffect(() => {
    if (!lastQuoteTime) {
      setQuoteAge("");
      setQuoteStale(false);
      return;
    }
    const update = () => {
      const secs = Math.round((Date.now() - lastQuoteTime) / 1000);
      setQuoteAge(secs < 5 ? "just now" : `${String(secs)}s ago`);
      setQuoteStale(secs >= 30);
    };
    update();
    const id = setInterval(update, 1000);
    return () => {
      clearInterval(id);
    };
  }, [lastQuoteTime]);

  // Auto-refresh quotes every 30s
  useEffect(() => {
    if (!amount || parseFloat(amount) <= 0 || !lastQuoteTime) return;
    const id = setInterval(refreshQuote, 30_000);
    return () => {
      clearInterval(id);
    };
  }, [amount, lastQuoteTime, refreshQuote]);

  // -------------------------------------------------------------------------
  // Token approval status check
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!amount || parseFloat(amount) <= 0 || !deployment || !address) {
      setApprovalStatus("unknown");
      return;
    }
    const tokenInAddr = getTokenAddress(tokenIn);
    if (!tokenInAddr) {
      setApprovalStatus("unknown");
      return;
    }

    let spender: string;
    if (protocol === "V2") spender = deployment.v2.router02;
    else if (protocol === "V3") spender = deployment.v3.router;
    else spender = deployment.v4.poolSwapTest;
    if (!isAddress(spender)) {
      setApprovalStatus("unknown");
      return;
    }

    const decimals = TOKEN_DECIMALS[tokenIn] ?? 18;
    let amountIn: bigint;
    try {
      amountIn = parseUnits(amount, decimals);
    } catch {
      setApprovalStatus("unknown");
      return;
    }

    const callData = encodeFunctionData({
      abi: MOCK_ERC20_ABI,
      functionName: "allowance",
      args: [address, spender],
    });
    anvilRequest("eth_call", [{ to: tokenInAddr, data: callData }, "latest"])
      .then((result) => {
        const strResult = result as string;
        if (!strResult || strResult === "0x") {
          setApprovalStatus("needs-approval");
          return;
        }
        const allowance = BigInt(strResult);
        setApprovalStatus(
          allowance >= amountIn ? "approved" : "needs-approval",
        );
      })
      .catch(() => {
        setApprovalStatus("unknown");
      });
  }, [tokenIn, amount, protocol, deployment, address]);

  const isValid =
    tokenIn !== tokenOut &&
    !!amount &&
    parseFloat(amount) > 0 &&
    isConnected &&
    !insufficientBalance &&
    !connectionError &&
    (protocol !== "V4" || v4PairHasPool) &&
    (protocol !== "V3" || v3PairHasPool);

  function handleSwap() {
    if (!canSwap) return;
    setShowConfirmation(true);
  }

  async function executeSwap() {
    setShowConfirmation(false);
    if (!walletClient || !address || !deployment?.tokens) return;

    const tokenInAddr = getTokenAddress(tokenIn);
    const tokenOutAddr = getTokenAddress(tokenOut);
    if (!tokenInAddr || !tokenOutAddr) return;

    const decimals = TOKEN_DECIMALS[tokenIn] ?? 18;
    const rawAmount = fullPrecisionAmount.current || amount;
    const amountIn = parseUnits(rawAmount, decimals);

    // Determine spender
    let spender: string;
    if (protocol === "V2") spender = deployment.v2.router02;
    else if (protocol === "V3") spender = deployment.v3.router;
    else spender = deployment.v4.poolSwapTest;
    if (!isAddress(spender)) return;

    const steps: StepDefinition[] = [];

    // Step 1: Approve tokenIn to spender
    steps.push({
      label: "Approve",
      execute: async () => {
        return walletClient.writeContract({
          chain,
          account: address,
          address: tokenInAddr,
          abi: MOCK_ERC20_ABI,
          functionName: "approve",
          args: [spender, MAX_UINT256],
        });
      },
      skip: async () => {
        const allowance = await publicClient.readContract({
          address: tokenInAddr,
          abi: MOCK_ERC20_ABI,
          functionName: "allowance",
          args: [address, spender],
        });
        return allowance >= amountIn;
      },
    });

    // Step 2: Swap (protocol-specific)
    if (protocol === "V2") {
      steps.push({
        label: "Swap",
        execute: async () => {
          return walletClient.writeContract({
            chain,
            account: address,
            address: deployment.v2.router02,
            abi: V2_ROUTER_ABI,
            functionName: "swapExactTokensForTokens",
            args: [
              amountIn,
              0n,
              [tokenInAddr, tokenOutAddr],
              address,
              deadline(),
            ],
          });
        },
      });
    } else if (protocol === "V3") {
      steps.push({
        label: "Swap",
        execute: async () => {
          const [token0] = sortTokens(tokenInAddr, tokenOutAddr);
          const zeroForOne = tokenInAddr.toLowerCase() === token0.toLowerCase();
          const sqrtPriceLimitX96 = zeroForOne
            ? MIN_SQRT_PRICE_LIMIT
            : MAX_SQRT_PRICE_LIMIT;

          if (useRouter02) {
            return walletClient.writeContract({
              chain,
              account: address,
              address: deployment.v3.router,
              abi: V3_SWAP_ROUTER02_ABI,
              functionName: "exactInputSingle",
              args: [
                {
                  tokenIn: tokenInAddr,
                  tokenOut: tokenOutAddr,
                  fee: parseInt(v3Fee),
                  recipient: address,
                  amountIn,
                  amountOutMinimum: 0n,
                  sqrtPriceLimitX96,
                },
              ],
            });
          }

          return walletClient.writeContract({
            chain,
            account: address,
            address: deployment.v3.router,
            abi: V3_SWAP_ROUTER_ABI,
            functionName: "exactInputSingle",
            args: [
              {
                tokenIn: tokenInAddr,
                tokenOut: tokenOutAddr,
                fee: parseInt(v3Fee),
                recipient: address,
                deadline: deadline(),
                amountIn,
                amountOutMinimum: 0n,
                sqrtPriceLimitX96,
              },
            ],
          });
        },
      });
    } else {
      // V4 — use matching pool config or selected pool config
      const poolConfig = matchingV4Pool ?? selectedV4Pool;
      steps.push({
        label: "Swap",
        execute: async () => {
          const [currency0, currency1] = sortTokens(tokenInAddr, tokenOutAddr);
          const zeroForOne =
            tokenInAddr.toLowerCase() === currency0.toLowerCase();
          const sqrtPriceLimitX96 = zeroForOne
            ? MIN_SQRT_PRICE_LIMIT
            : MAX_SQRT_PRICE_LIMIT;

          return walletClient.writeContract({
            chain,
            account: address,
            address: deployment.v4.poolSwapTest,
            abi: POOL_SWAP_TEST_ABI,
            functionName: "swap",
            args: [
              {
                currency0,
                currency1,
                fee: poolConfig.fee,
                tickSpacing: poolConfig.tickSpacing,
                hooks: ZERO_ADDRESS,
              },
              {
                zeroForOne,
                amountSpecified: -amountIn,
                sqrtPriceLimitX96,
              },
              { takeClaims: false, settleUsingBurn: false },
              "0x" as Hex,
            ],
          });
        },
      });
    }

    const swapRecord = {
      tokenIn,
      tokenOut,
      amount,
      amountOut: outputNumeric > 0 ? String(outputNumeric) : undefined,
      usdValue: parseFloat(amount) * (DEVENV_TOKEN_PRICES[tokenIn] ?? 0),
      protocol,
      timestamp: Date.now(),
    };

    setSwapButtonState("swapping");
    const displayIn = formatDisplayAmount(amount).display;
    const loadingToast = toast.loading(
      `Swapping ~${displayIn} ${tokenIn} for ${tokenOut}...`,
    );

    await flow.execute(
      steps,
      () => {
        toast.dismiss(loadingToast);
        const displayOut =
          outputNumeric > 0 ? formatDisplayAmount(outputNumeric).display : "";
        toast.success(
          `Swapped ~${displayIn} ${tokenIn}${displayOut ? ` for ~${displayOut} ${tokenOut}` : ` for ${tokenOut}`}`,
        );
        // Capture tx hash from the swap step (or last step)
        const swapStepHash =
          flow.steps.find(
            (s) => s.label.includes("Swap") || s.label.includes("Liquidity"),
          )?.hash ?? flow.steps[flow.steps.length - 1]?.hash;
        setRecentSwaps((prev) =>
          [{ ...swapRecord, hash: swapStepHash }, ...prev].slice(0, 20),
        );
        setSwapButtonState("swapped");
        onTxSuccess();
        setTimeout(() => {
          setSwapButtonState("idle");
          setAmount("");
          fullPrecisionAmount.current = "";
        }, 2000);
        refetchBalances();
      },
      (label, err) => {
        toast.dismiss(loadingToast);
        toast.error(`${label} failed: ${err}`);
        onTxError();
        setSwapButtonState("idle");
      },
    );
  }

  // Numeric output amount for USD calculations
  const outputNumeric = useMemo(() => {
    if (protocol === "V2" && estimatedOutput)
      return parseFloat(estimatedOutput);
    if (protocol === "V3" && v3Quote) return parseFloat(v3Quote);
    if (protocol === "V4" && v4Quote) return parseFloat(v4Quote);
    return 0;
  }, [protocol, estimatedOutput, v3Quote, v4Quote]);

  // Exchange rate (forward and reverse)
  const exchangeRate = useMemo(() => {
    const inputNum = parseFloat(amount);
    if (!inputNum || inputNum === 0 || outputNumeric === 0) return null;
    const forward = outputNumeric / inputNum;
    const reverse = inputNum / outputNumeric;
    return { forward, reverse };
  }, [amount, outputNumeric]);

  // V3 price impact vs reference prices
  const v3PriceImpact = useMemo(() => {
    if (protocol !== "V3" || !v3Quote || !amount) return null;
    const inputNum = parseFloat(amount);
    if (!inputNum || inputNum === 0) return null;
    const midRate =
      (DEVENV_TOKEN_PRICES[tokenIn] ?? 0) /
      (DEVENV_TOKEN_PRICES[tokenOut] ?? 1);
    if (midRate === 0) return null;
    const actualRate = outputNumeric / inputNum;
    const impact = (1 - actualRate / midRate) * 100;
    return Math.abs(impact);
  }, [protocol, v3Quote, amount, tokenIn, tokenOut, outputNumeric]);

  // Combined price impact (V2 from reserves, V3 from reference)
  const effectivePriceImpact = useMemo(() => {
    if (typeof priceImpactLabel === "number") return priceImpactLabel;
    if (v3PriceImpact !== null) return v3PriceImpact;
    return null;
  }, [priceImpactLabel, v3PriceImpact]);

  const isQuoteLoading =
    (protocol === "V3" && v3QuoteLoading) ||
    (protocol === "V4" && v4QuoteLoading);

  const hasV4ExecutableFallback = useMemo(() => {
    if (protocol !== "V4") return false;
    if (!amount || parseFloat(amount) <= 0) return false;
    if (!swapCalldata) return false;

    // If calldata is built and gas estimation did not fail, the swap can be
    // constructed/executed even when V4 quoter cannot return output.
    return gasEstimate !== "estimate failed";
  }, [protocol, amount, swapCalldata, gasEstimate]);

  const hasExecutableQuote = useMemo(() => {
    if (!amount || parseFloat(amount) <= 0) return false;

    if (protocol === "V2") {
      return outputNumeric > 0;
    }

    if (protocol === "V3") {
      return !v3QuoteLoading && outputNumeric > 0;
    }

    return !v4QuoteLoading && (outputNumeric > 0 || hasV4ExecutableFallback);
  }, [
    amount,
    outputNumeric,
    protocol,
    v3QuoteLoading,
    v4QuoteLoading,
    hasV4ExecutableFallback,
  ]);

  // Best protocol indicator (compare V2 vs V3 output)
  const bestProtocol = useMemo((): Protocol | null => {
    const v2Num = estimatedOutput ? parseFloat(estimatedOutput) : 0;
    const v3Num = v3Quote ? parseFloat(v3Quote) : 0;
    if (v2Num > 0 && v3Num > 0) return v2Num >= v3Num ? "V2" : "V3";
    if (v2Num > 0) return "V2";
    if (v3Num > 0) return "V3";
    return null;
  }, [estimatedOutput, v3Quote]);

  // Minimum received (after slippage)
  const minimumReceived = useMemo(() => {
    if (outputNumeric <= 0) return null;
    return computeMinimumReceived(outputNumeric, effectiveSlippageBps);
  }, [outputNumeric, effectiveSlippageBps]);

  // Compute stepper currentStep for display
  const stepperCurrent = flow.steps.filter(
    (s) => s.status === "success" || s.status === "skipped",
  ).length;
  const hasError = flow.steps.some((s) => s.status === "error");
  const errorStep = flow.steps.find((s) => s.status === "error");

  // Smart button label
  const buttonLabel = useMemo(() => {
    if (!amount || parseFloat(amount) === 0) return "Enter an amount";
    if (insufficientBalance) return `Insufficient ${tokenIn} balance`;
    if (tokenIn === tokenOut) return "Select different tokens";
    if (protocol === "V4" && !v4PairHasPool) return "No pool available";
    if (protocol === "V3" && !v3FeesLoaded) return "Loading fee tiers...";
    if (protocol === "V3" && !v3PairHasPool) return "No pool at this fee tier";
    if (isQuoteLoading) return "Fetching quote...";
    if (protocol === "V4" && hasV4ExecutableFallback && outputNumeric <= 0)
      return "Swap (no quote)";
    if (!hasExecutableQuote) return "Quote unavailable";
    if (flow.isExecuting) return "Confirming...";
    if (swapButtonState === "swapping") return "Swapping...";
    if (swapButtonState === "swapped") return "\u2713 Swap complete";
    if (effectivePriceImpact !== null && effectivePriceImpact > 5)
      return "Swap Anyway";
    // Summary label when output is available
    if (outputNumeric > 0) {
      return formatSwapButtonLabel(amount, tokenIn, outputNumeric, tokenOut);
    }
    return "Swap";
  }, [
    amount,
    insufficientBalance,
    tokenIn,
    tokenOut,
    protocol,
    v4PairHasPool,
    v3FeesLoaded,
    v3PairHasPool,
    isQuoteLoading,
    hasV4ExecutableFallback,
    hasExecutableQuote,
    flow.isExecuting,
    swapButtonState,
    effectivePriceImpact,
    outputNumeric,
  ]);

  const canSwap =
    buttonLabel !== "Enter an amount" &&
    !insufficientBalance &&
    tokenIn !== tokenOut &&
    (protocol !== "V4" || v4PairHasPool) &&
    (protocol !== "V3" || v3FeesLoaded) &&
    (protocol !== "V3" || v3PairHasPool) &&
    !isQuoteLoading &&
    hasExecutableQuote &&
    swapButtonState !== "swapping" &&
    swapButtonState !== "swapped" &&
    !flow.isExecuting &&
    !!amount &&
    parseFloat(amount) > 0;

  // Price impact color: green < 1%, yellow 1-3%, orange 3-5%, red > 5%
  const priceImpactColor = useMemo(() => {
    const impact = effectivePriceImpact;
    if (impact === null) return undefined;
    if (impact < 1) return "text-green-400";
    if (impact <= 3) return "text-yellow-400";
    if (impact <= 5) return "text-orange-400";
    return "text-red-400";
  }, [effectivePriceImpact]);

  // LP fee summary — protocol-aware
  // V3/V4 fee values are in millionths (3000 = 0.30%), V2 is a fixed 0.30%.
  const feeSummary = useMemo(() => {
    let bps: number;
    let feeRate: number;
    let color: string;
    if (protocol === "V2") {
      bps = 30;
      feeRate = 0.003; // 0.30% fixed
      color = "text-white/60";
    } else if (protocol === "V3") {
      bps = parseInt(v3Fee);
      feeRate = bps / 1_000_000;
      color =
        bps <= 5
          ? "text-green-400"
          : bps <= 30
            ? "text-white/60"
            : bps >= 100
              ? "text-amber-400"
              : "text-white/60";
    } else {
      // V4
      const poolConfig = matchingV4Pool ?? selectedV4Pool;
      bps = poolConfig.fee;
      feeRate = bps / 1_000_000;
      color =
        bps <= 5
          ? "text-green-400"
          : bps <= 30
            ? "text-white/60"
            : bps >= 100
              ? "text-amber-400"
              : "text-white/60";
    }
    const pct = (feeRate * 100).toFixed(2) + "%";

    // Fee in token terms (only when user has entered an amount)
    const parsedAmount = parseFloat(amount);
    const tokenAmount = parsedAmount > 0 ? parsedAmount * feeRate : 0;
    const usdPrice = DEVENV_TOKEN_PRICES[tokenIn] ?? 0;
    const usdAmount = tokenAmount * usdPrice;

    return { bps, pct, color, tokenAmount, usdAmount };
  }, [protocol, v3Fee, matchingV4Pool, selectedV4Pool, amount, tokenIn]);

  // Precompute version comparison for smooth reveal (avoids inline IIFE)
  const versionComparison = useMemo(() => {
    if (!amount || parseFloat(amount) <= 0)
      return {
        visible: false,
        quotes: [] as { proto: Protocol; output: number }[],
      };
    const v2Num = estimatedOutput ? parseFloat(estimatedOutput) : 0;
    const v3Num = v3Quote ? parseFloat(v3Quote) : 0;
    const quotes = [
      { proto: "V2" as Protocol, output: v2Num },
      { proto: "V3" as Protocol, output: v3Num },
      {
        proto: "V4" as Protocol,
        output: v4Quote ? parseFloat(v4Quote) : 0,
      },
    ].filter((r) => r.output > 0 || r.proto === protocol);
    const hasMultiple = quotes.filter((r) => r.output > 0).length >= 2;
    const visible = hasMultiple || quotes.length > 1;
    return { visible, quotes };
  }, [amount, estimatedOutput, v3Quote, v4Quote, protocol]);

  // Whether the approval/insufficient-balance area should be revealed
  const showStatusArea =
    (approvalStatus !== "unknown" && !!amount && parseFloat(amount) > 0) ||
    insufficientBalance;

  return (
    <div>
      <PcbCard
        className={cn(
          "mx-auto w-full max-w-[520px] [&_.pcb-card-inner]:p-0",
          feedbackClass,
        )}
        cut={18}
      >
        {connectionError && (
          <Alert
            variant="error"
            title="Anvil Unreachable"
            className="mx-3 mt-3"
          >
            Cannot connect to Anvil at 127.0.0.1:8545. Is it running?
          </Alert>
        )}

        {/* ---- Card header ---- */}
        <div className="flex items-center justify-between px-4 pb-0 pt-4">
          <span className="font-serif text-xl font-light text-white/90">
            Swap
          </span>
          <div className="flex items-center gap-1">
            {PROTOCOLS.map((p) => {
              const baseTooltip =
                p === "V2"
                  ? "Constant product AMM"
                  : p === "V3"
                    ? "Concentrated liquidity, usually best price"
                    : "Hook-based, customizable";
              const quoteOutput =
                p === "V2" && estimatedOutput
                  ? parseFloat(estimatedOutput)
                  : p === "V3" && v3Quote
                    ? parseFloat(v3Quote)
                    : 0;
              const tooltipText =
                quoteOutput > 0
                  ? `${baseTooltip}: ~${formatDisplayAmount(quoteOutput).display} ${tokenOut}`
                  : baseTooltip;
              return (
                <Tooltip key={p} content={tooltipText}>
                  <button
                    type="button"
                    onClick={() => {
                      setProtocol(p);
                    }}
                    className={cn(
                      "relative rounded-sm px-2 py-1 font-mono text-[13px] font-medium transition-colors",
                      protocol === p
                        ? "bg-white/10 text-white"
                        : "text-white/40 hover:text-white/60",
                    )}
                  >
                    {p}
                    {bestProtocol === p && amount && parseFloat(amount) > 0 && (
                      <Tooltip content="Best rate among available protocols">
                        <span className="absolute -right-1.5 -top-1.5 scale-[0.6]">
                          <Badge variant="success">Best</Badge>
                        </span>
                      </Tooltip>
                    )}
                  </button>
                </Tooltip>
              );
            })}
            <button
              type="button"
              onClick={() => {
                setShowSettings((v) => !v);
              }}
              className={cn(
                "ml-1 flex h-7 w-7 items-center justify-center rounded-full text-sm transition-colors",
                showSettings
                  ? "bg-white/10 text-white/80"
                  : "text-white/40 hover:bg-white/10 hover:text-white/60",
              )}
              data-testid="settings-gear"
              title="Slippage settings"
            >
              {"\u2699"}
            </button>
          </div>
        </div>

        {/* ---- Settings popover (slippage) ---- */}
        {showSettings && (
          <div
            className={cn(
              "octa-md mx-3 mt-2 bg-[rgba(255,255,255,0.03)] p-3",
              effectiveSlippageBps > 200 && "bg-[rgba(245,158,11,0.04)]",
            )}
            data-testid="settings-panel"
          >
            <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--color-text-muted)]">
              Slippage tolerance
            </div>
            <div className="flex items-center gap-2">
              {SLIPPAGE_PRESETS.map((preset) => (
                <Button
                  key={preset.bps}
                  variant={
                    !useCustomSlippage && slippageBps === preset.bps
                      ? "gold"
                      : "outline"
                  }
                  size="sm"
                  onClick={() => {
                    setSlippageBps(preset.bps);
                    setUseCustomSlippage(false);
                    setCustomSlippage("");
                  }}
                >
                  {preset.label}
                </Button>
              ))}
              <div className="relative flex items-center">
                <Input
                  type="number"
                  placeholder="Custom %"
                  value={customSlippage}
                  onChange={(e) => {
                    setCustomSlippage(e.target.value);
                    setUseCustomSlippage(true);
                  }}
                  className="w-20 pr-5"
                />
                <span className="pointer-events-none absolute right-2 text-[10px] text-white/30">
                  %
                </span>
              </div>
            </div>
            {effectiveSlippageBps > 200 && effectiveSlippageBps <= 500 && (
              <p className="mt-1.5 text-[10px] text-[var(--color-warning)]">
                High slippage ({(effectiveSlippageBps / 100).toFixed(1)}%){" "}
                {"\u2014"} front-running risk
              </p>
            )}
            {effectiveSlippageBps > 500 && (
              <p className="mt-1.5 text-[10px] text-[var(--color-warning)]">
                Very high slippage ({(effectiveSlippageBps / 100).toFixed(1)}%){" "}
                {"\u2014"} you may lose significant value
              </p>
            )}
          </div>
        )}

        {/* ---- You pay ---- */}
        <div className="octa-md mx-3 mt-3 bg-[rgba(255,255,255,0.03)] p-4">
          <div className="mb-2 font-mono text-xs uppercase tracking-[0.12em] text-[var(--color-text-muted)]">
            You pay
          </div>
          <div className="flex items-center justify-between gap-3">
            <input
              type="number"
              placeholder="0"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                fullPrecisionAmount.current = "";
              }}
              className="amount-input w-0 flex-1 text-white outline-none placeholder:text-white/20"
              aria-label="Amount"
            />
            <TokenPill
              symbol={tokenIn}
              onChange={(s) => {
                setTokenIn(s);
              }}
              testId="token-in-pill"
              balances={balances as Record<string, { formatted: string }>}
            />
          </div>
          <div className="mt-2 flex items-center justify-between">
            {amount && parseFloat(amount) > 0 ? (
              <span className="font-mono text-[11px] text-white/40">
                <AnimatedBalance
                  value={
                    parseFloat(amount || "0") *
                    (DEVENV_TOKEN_PRICES[tokenIn] ?? 0)
                  }
                  format={formatUsd}
                  emDash={false}
                />
              </span>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-1.5">
              <span
                className="font-mono text-[11px] text-white/50"
                title={tokenInBalance?.formatted}
              >
                Balance:{" "}
                {balancesLoading && !tokenInBalance ? (
                  <span className="inline-block h-3 w-16 animate-pulse rounded-sm bg-white/10 align-middle" />
                ) : (
                  <>
                    {tokenInBalance
                      ? formatDisplayAmount(tokenInBalance.formatted).display
                      : "0"}{" "}
                    {tokenIn}
                  </>
                )}
              </span>
              <button
                type="button"
                onClick={() => {
                  if (tokenInBalance) {
                    fullPrecisionAmount.current = tokenInBalance.formatted;
                    setAmount(
                      formatDisplayAmount(tokenInBalance.formatted).display,
                    );
                  }
                }}
                disabled={!tokenInBalance || tokenInBalance.raw === 0n}
                className="btn-max-pill disabled:cursor-default disabled:opacity-30"
              >
                MAX
              </button>
            </div>
          </div>
          <div
            className={cn(
              "grid transition-[grid-template-rows] duration-150 ease-out",
              showStatusArea ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
            )}
          >
            <div className="overflow-hidden">
              {approvalStatus === "approved" &&
                amount &&
                parseFloat(amount) > 0 && (
                  <span className="mt-1 block text-[10px] text-[var(--color-success)]">
                    Approved {"\u2713"}
                  </span>
                )}
              {approvalStatus === "needs-approval" &&
                amount &&
                parseFloat(amount) > 0 && (
                  <span className="mt-1 block text-[10px] text-[var(--color-warning)]">
                    Approval needed
                  </span>
                )}
              {insufficientBalance && (
                <p
                  className="mt-1 text-xs text-[var(--color-error)]"
                  data-testid="insufficient-balance"
                >
                  Insufficient {tokenIn} balance
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ---- Swap direction + exchange rate bar ---- */}
        <div className="mx-3 my-1">
          <div className="flex items-center gap-2">
            {/* Left: token-in icon */}
            <TokenIcon symbol={tokenIn} size={20} />

            {/* Center: rate + swap toggle */}
            <div className="flex flex-1 items-center justify-center">
              {/* Rate display (fades in/out) */}
              <Tooltip content="Click to flip rate">
                <button
                  type="button"
                  onClick={() => {
                    setRateFlipped((v) => !v);
                  }}
                  className={cn(
                    "font-mono text-xs transition-all duration-200",
                    exchangeRate
                      ? "text-white/45 hover:text-white/70"
                      : "pointer-events-none text-white/15",
                  )}
                  data-testid="exchange-rate"
                >
                  {exchangeRate ? (
                    rateFlipped ? (
                      <>
                        1 {tokenOut} ={" "}
                        <AnimatedBalance
                          value={exchangeRate.reverse}
                          format={(n) => formatTokenDisplay(n).display}
                          emDash={false}
                        />{" "}
                        {tokenIn}
                      </>
                    ) : (
                      <>
                        1 {tokenIn} ={" "}
                        <AnimatedBalance
                          value={exchangeRate.forward}
                          format={(n) => formatTokenDisplay(n).display}
                          emDash={false}
                        />{" "}
                        {tokenOut}
                      </>
                    )
                  ) : (
                    <span className="flex items-center gap-1.5">
                      {tokenIn}
                      <span className="text-[var(--color-gold)]/40">
                        {"\u2192"}
                      </span>
                      {tokenOut}
                    </span>
                  )}
                </button>
              </Tooltip>

              {/* Quote age */}
              {quoteAge && (
                <span
                  className={cn(
                    "ml-1.5 text-[9px]",
                    quoteStale
                      ? "text-[var(--color-warning)]"
                      : "text-white/20",
                  )}
                >
                  {quoteAge}
                </span>
              )}

              {/* Refresh button (only when rate exists) */}
              {exchangeRate && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    refreshQuote();
                  }}
                  className="ml-1 text-[11px] text-white/25 transition-colors hover:text-white/60"
                  title="Refresh quote"
                  data-testid="refresh-quote"
                >
                  {"\u21BB"}
                </button>
              )}
            </div>

            {/* Swap direction toggle */}
            <button
              type="button"
              onClick={() => {
                setTokenIn(tokenOut);
                setTokenOut(tokenIn);
              }}
              className="border-[var(--color-gold)]/15 text-[var(--color-gold)]/50 hover:border-[var(--color-gold)]/40 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full border bg-[#1a1e26] text-sm transition-all duration-300 hover:text-[var(--color-gold)] hover:shadow-[0_0_8px_rgba(201,168,76,0.12)]"
              data-testid="swap-direction"
              title="Swap direction"
            >
              {"\u21C5"}
            </button>

            {/* Right: token-out icon */}
            <TokenIcon symbol={tokenOut} size={20} />
          </div>
        </div>

        {/* ---- Inline version comparison ---- */}
        <div
          className={cn(
            "grid transition-[grid-template-rows] duration-200 ease-out",
            versionComparison.visible ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
          )}
        >
          <div className="overflow-hidden">
            {versionComparison.visible && (
              <div className="octa-sm mx-3 -mt-1 mb-1 bg-[rgba(255,255,255,0.02)] px-3 py-2 text-[11px]">
                {versionComparison.quotes.map((r) => (
                  <div key={r.proto} className="flex justify-between">
                    <span className="text-white/40">{r.proto}</span>
                    <span className="font-mono text-white/50">
                      {r.output > 0
                        ? `${formatTokenDisplay(r.output).display} ${tokenOut}`
                        : "Quote on-chain"}
                      {r.proto === bestProtocol && " \u2605"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ---- You receive ---- */}
        <div className="octa-md mx-3 bg-[rgba(255,255,255,0.03)] p-4">
          <div className="mb-2 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.12em] text-[var(--color-text-muted)]">
            You receive{" "}
            {(v3QuoteLoading && protocol === "V3") ||
            (v4QuoteLoading && protocol === "V4") ? (
              <span className="text-[10px] normal-case tracking-normal text-white/40">
                fetching quote...
              </span>
            ) : (
              <Tooltip content="Final amount depends on price movement and slippage tolerance">
                <span className="cursor-help border-b border-dashed border-white/20 text-[10px] normal-case tracking-normal text-white/25">
                  estimated
                </span>
              </Tooltip>
            )}
          </div>
          <div className="flex items-center justify-between gap-3">
            {(() => {
              const isQuoting =
                (v3QuoteLoading && protocol === "V3") ||
                (v4QuoteLoading && protocol === "V4");
              const rawOutput =
                !amount || parseFloat(amount) === 0
                  ? null
                  : protocol === "V2"
                    ? estimatedOutput
                    : protocol === "V3"
                      ? v3QuoteLoading
                        ? null
                        : v3Quote
                      : v4QuoteLoading
                        ? null
                        : v4Quote;
              const formatted = rawOutput
                ? formatTokenDisplay(rawOutput)
                : null;
              if (isQuoting) {
                return (
                  <div
                    className="flex flex-col gap-1"
                    data-testid="estimated-output"
                  >
                    <span className="inline-block h-7 w-28 animate-pulse rounded-sm bg-white/10" />
                  </div>
                );
              }
              return (
                <span
                  className={cn(
                    "amount-input",
                    !rawOutput ? "text-white/20" : "text-white/60",
                  )}
                  data-testid="estimated-output"
                  title={formatted?.full}
                >
                  {!amount || parseFloat(amount) === 0
                    ? "0"
                    : (formatted?.display ?? "\u2014")}
                </span>
              );
            })()}
            <TokenPill
              symbol={tokenOut}
              onChange={(s) => {
                setTokenOut(s);
              }}
              testId="token-out-pill"
              balances={balances as Record<string, { formatted: string }>}
            />
          </div>
          <div className="mt-2 flex items-center justify-between">
            <div>
              {outputNumeric > 0 ? (
                <span className="font-mono text-[11px] text-white/40">
                  ~
                  <AnimatedBalance
                    value={outputNumeric * (DEVENV_TOKEN_PRICES[tokenOut] ?? 0)}
                    format={formatUsd}
                    emDash={false}
                  />
                </span>
              ) : (
                <span />
              )}
            </div>
            <div className="flex items-center gap-2">
              <span
                className="font-mono text-[11px] text-white/50"
                title={tokenOutBalance?.formatted}
              >
                Balance:{" "}
                {balancesLoading && !tokenOutBalance ? (
                  <span className="inline-block h-3 w-16 animate-pulse rounded-sm bg-white/10 align-middle" />
                ) : (
                  <>
                    {tokenOutBalance
                      ? formatDisplayAmount(tokenOutBalance.formatted).display
                      : "0"}{" "}
                    {tokenOut}
                  </>
                )}
              </span>
            </div>
          </div>
          <div
            className={cn(
              "grid transition-[grid-template-rows] duration-150 ease-out",
              minimumReceived !== null ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
            )}
          >
            <div className="overflow-hidden">
              {minimumReceived !== null && (
                <div className="mt-1 font-mono text-[11px] text-white/30">
                  Min: {formatTokenDisplay(minimumReceived).display} {tokenOut}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ---- V4 no-pool warning (always visible when relevant) ---- */}
        {protocol === "V4" && !v4PairHasPool && (
          <div className="mx-3 mt-2">
            <Alert variant="warning" title="No V4 Pool">
              No V4 pool for {tokenIn}/{tokenOut}. Seeded: WETH/USDC, WETH/DAI.
            </Alert>
          </div>
        )}

        {/* ---- V3 no-pool warning ---- */}
        {protocol === "V3" && !v3PairHasPool && (
          <div className="mx-3 mt-2">
            <Alert variant="warning" title="No V3 Pool">
              No V3 pool for {tokenIn}/{tokenOut} at{" "}
              {V3_FEE_TIERS.find((f) => f.value === parseInt(v3Fee))?.label ??
                `${v3Fee} bps`}
              . Try a different fee tier.
            </Alert>
          </div>
        )}

        {/* ---- High price impact warning ---- */}
        {effectivePriceImpact !== null && effectivePriceImpact > 5 && (
          <div className="mx-3 mt-2">
            <Alert variant="error" title="High Price Impact">
              This swap has a {effectivePriceImpact.toFixed(2)}% price impact.
              You may receive significantly fewer tokens than expected.
            </Alert>
          </div>
        )}

        {/* ---- Details row ---- */}
        <div className="mx-3 mt-1">
          <button
            type="button"
            onClick={() => {
              setShowDetails((v) => !v);
            }}
            className="flex w-full items-center justify-between px-1 py-2 text-xs text-white/50 transition-colors hover:text-white/60"
            data-testid="details-toggle"
          >
            <span className="flex items-center gap-1.5 truncate">
              <TokenIcon symbol={tokenIn} size={16} />
              <span>{tokenIn}</span>
              <span className="text-white/30">{"\u2192"}</span>
              <ProtocolBadge protocol={protocol} />
              <Badge
                variant={
                  protocol === "V2"
                    ? "info"
                    : protocol === "V3"
                      ? "success"
                      : undefined
                }
              >
                {feeSummary.pct}
              </Badge>
              <span className="text-white/30">{"\u2192"}</span>
              <TokenIcon symbol={tokenOut} size={16} />
              <span>{tokenOut}</span>
            </span>
            <div className="flex shrink-0 items-center gap-2">
              {feeSummary.tokenAmount > 0 && (
                <span
                  className="ml-1 font-mono text-white/50"
                  data-testid="fee-token-amount"
                >
                  <AnimatedBalance
                    value={feeSummary.usdAmount}
                    format={formatUsd}
                    emDash={false}
                  />{" "}
                  <span className="text-white/30">{feeSummary.pct}</span>
                </span>
              )}
              {effectivePriceImpact !== null && (
                <span data-testid="price-impact" className={priceImpactColor}>
                  <AnimatedBalance
                    value={effectivePriceImpact}
                    format={(n) => n.toFixed(2)}
                    suffix="%"
                    emDash={false}
                  />
                </span>
              )}
              {gasCost && (
                <span className="text-white/30">
                  ~
                  <AnimatedBalance
                    value={gasCost.usdCost}
                    format={formatUsd}
                    emDash={false}
                  />{" "}
                  gas
                </span>
              )}
              <span className="text-white/30">
                {(effectiveSlippageBps / 100).toFixed(1)}% slip
              </span>
              <span
                className={cn(
                  "text-sm transition-transform",
                  showDetails && "rotate-180",
                )}
              >
                {"\u25BC"}
              </span>
            </div>
          </button>

          {showDetails && (
            <div className="space-y-2 px-1 pb-2 text-xs text-white/50">
              {/* V3 Fee Tier */}
              {protocol === "V3" && (
                <FormField label="Fee Tier">
                  <Select
                    options={feeOptions}
                    value={v3Fee}
                    onValueChange={setV3Fee}
                  />
                </FormField>
              )}

              {/* V4 Pool Config */}
              {protocol === "V4" && (
                <FormField label="V4 Pool">
                  <Select
                    options={v4PoolOptions}
                    value={v4Pool}
                    onValueChange={setV4Pool}
                  />
                </FormField>
              )}

              {/* Swap fee (renamed from LP fee) */}
              <div className="flex items-center justify-between">
                <Tooltip content="Fee paid to liquidity providers">
                  <span className="cursor-help border-b border-dashed border-white/20">
                    Swap fee
                  </span>
                </Tooltip>
                <div className="text-right">
                  {feeSummary.tokenAmount > 0 ? (
                    <span
                      className="font-mono font-medium text-white/70"
                      data-testid="fee-detail-amount"
                    >
                      <AnimatedBalance
                        value={feeSummary.usdAmount}
                        format={formatUsd}
                        emDash={false}
                      />
                      <span className="ml-1 text-white/30">
                        {feeSummary.pct}
                      </span>
                    </span>
                  ) : (
                    <span className={`font-mono ${feeSummary.color}`}>
                      {feeSummary.pct}
                    </span>
                  )}
                </div>
              </div>

              {/* Price impact */}
              {effectivePriceImpact !== null && (
                <div className="flex items-center justify-between">
                  <Tooltip
                    content={
                      protocol === "V3"
                        ? "vs. reference price"
                        : protocol === "V4"
                          ? "Requires on-chain simulation"
                          : "Based on pool reserves"
                    }
                  >
                    <span className="cursor-help border-b border-dashed border-white/20">
                      Price impact
                    </span>
                  </Tooltip>
                  <span className={`font-mono ${priceImpactColor ?? ""}`}>
                    <AnimatedBalance
                      value={effectivePriceImpact}
                      format={(n) => n.toFixed(2)}
                      suffix="%"
                      emDash={false}
                    />
                  </span>
                </div>
              )}
              {protocol === "V4" &&
                effectivePriceImpact === null &&
                amount &&
                parseFloat(amount) > 0 && (
                  <div className="flex items-center justify-between">
                    <span>Price impact</span>
                    <Tooltip content="Requires on-chain simulation">
                      <span className="font-mono text-[var(--color-warning)]">
                        {"\u26A0"} On-chain only
                      </span>
                    </Tooltip>
                  </div>
                )}

              {/* Minimum received */}
              {minimumReceived !== null && (
                <div className="flex items-center justify-between">
                  <span>Minimum received</span>
                  <span className="font-mono text-white/60">
                    {formatTokenDisplay(minimumReceived).display} {tokenOut}
                  </span>
                </div>
              )}

              {/* Network fee (renamed from Gas estimate) */}
              {gasEstimate && (
                <div className="flex items-center justify-between">
                  <span>Network fee</span>
                  <span className="font-mono" data-testid="gas-cost-eth">
                    {gasCost ? (
                      <>
                        ~
                        <AnimatedBalance
                          value={gasCost.usdCost}
                          format={formatUsd}
                          emDash={false}
                        />
                        <span
                          className="ml-1 text-white/30"
                          data-testid="gas-cost-units"
                        >
                          {gasEstimate} gas
                        </span>
                      </>
                    ) : (
                      <span>{gasEstimate}</span>
                    )}
                  </span>
                </div>
              )}

              {/* Slippage tolerance (read-only, links to settings) */}
              <div className="flex items-center justify-between">
                <span>Slippage tolerance</span>
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-white/60">
                    {(effectiveSlippageBps / 100).toFixed(1)}%
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSettings(true);
                    }}
                    className="hover:text-[var(--color-gold)]/80 text-[10px] text-[var(--color-gold)] transition-colors"
                  >
                    edit
                  </button>
                </div>
              </div>

              {/* Total cost */}
              {(feeSummary.usdAmount > 0 || gasCost) && (
                <div className="flex items-center justify-between pt-1.5 text-white/60">
                  <span className="font-medium">Total cost</span>
                  <span className="font-mono font-medium">
                    ~
                    <AnimatedBalance
                      value={feeSummary.usdAmount + (gasCost?.usdCost ?? 0)}
                      format={formatUsd}
                      emDash={false}
                    />
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ---- Advanced ---- */}
        <div className="mx-3">
          <CollapsibleSection title="Dev Details" defaultOpen={false}>
            {deployment && (
              <div className="text-text-muted mb-2 flex items-center gap-1 text-xs">
                <span>Router:</span>
                {(() => {
                  const routerAddr = getRouterAddress();
                  return routerAddr ? (
                    <>
                      <AddressDisplay address={routerAddr} fontSize={10} />
                      <InlineCopy text={routerAddr} />
                    </>
                  ) : (
                    <span>N/A</span>
                  );
                })()}
              </div>
            )}
            {swapCalldata && (
              <CollapsibleSection title="Calldata" defaultOpen={false}>
                {/* Decoded function signature */}
                <div className="mb-2 font-mono text-[10px]">
                  <span className="text-[var(--color-gold)]">
                    {protocol === "V2"
                      ? "swapExactTokensForTokens"
                      : protocol === "V3"
                        ? "exactInputSingle"
                        : "swap"}
                  </span>
                  <span className="text-white/30">
                    ({tokenIn} {"\u2192"} {tokenOut}, {amount} in
                    {protocol === "V3" ? `, fee=${v3Fee}` : ""}
                    {protocol === "V4"
                      ? `, fee=${String((matchingV4Pool ?? selectedV4Pool).fee)}, ts=${String((matchingV4Pool ?? selectedV4Pool).tickSpacing)}`
                      : ""}
                    )
                  </span>
                </div>
                <div className="relative">
                  <div className="absolute right-1 top-1">
                    <InlineCopy text={swapCalldata} />
                  </div>
                  <pre className="max-h-32 overflow-auto rounded bg-black/20 p-2 pr-7">
                    <code className="break-all font-mono text-xs">
                      {swapCalldata}
                    </code>
                  </pre>
                </div>
              </CollapsibleSection>
            )}
          </CollapsibleSection>
        </div>

        {/* Stepper (visible during execution) */}
        <div
          className={cn(
            "grid transition-[grid-template-rows] duration-200 ease-out",
            flow.steps.length > 0 ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
          )}
        >
          <div className="overflow-hidden">
            {flow.steps.length > 0 && (
              <div className="mx-3 mb-2">
                <Stepper
                  steps={flow.steps.map((s) => s.label)}
                  currentStep={stepperCurrent}
                />
                {flow.steps.some((s) => s.hash) && (
                  <div className="mt-2 space-y-1">
                    {flow.steps.map(
                      (step, i) =>
                        step.hash && (
                          <div
                            key={i}
                            className="text-text-muted flex items-center gap-1 text-xs"
                          >
                            <span>{step.label} tx:</span>
                            <code
                              title="View in Transaction Log"
                              className="cursor-pointer font-mono hover:text-[var(--color-gold)]"
                              onClick={() =>
                                navigate(
                                  `/transactions?hash=${step.hash ?? ""}`,
                                )
                              }
                            >
                              {truncateAddress(step.hash)}
                            </code>
                            <InlineCopy text={step.hash} />
                          </div>
                        ),
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Error display */}
        <div
          className={cn(
            "grid transition-[grid-template-rows] duration-200 ease-out",
            hasError && errorStep ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
          )}
        >
          <div className="overflow-hidden">
            {hasError && errorStep && (
              <div className="mx-3 mb-2">
                <Alert variant="error" title="Transaction Failed">
                  {errorStep.error}
                  {errorStep.hash && (
                    <div className="mt-1 font-mono text-xs">
                      Tx:{" "}
                      <code
                        title={`Click to copy: ${errorStep.hash}`}
                        className="cursor-pointer hover:text-[var(--color-gold)]"
                        onClick={() => {
                          if (errorStep.hash)
                            void navigator.clipboard.writeText(errorStep.hash);
                        }}
                      >
                        {truncateAddress(errorStep.hash)}
                      </code>
                    </div>
                  )}
                </Alert>
              </div>
            )}
          </div>
        </div>

        {/* ---- Recent Swaps (inline) ---- */}
        {recentSwaps.length > 0 && (
          <div className="mx-3 mb-1">
            <CollapsibleSection
              title={`Recent Swaps (${String(recentSwaps.length)})`}
              defaultOpen={false}
            >
              <div className="space-y-1.5">
                {recentSwaps.slice(0, 5).map((swap, i) => {
                  const explorerUrl =
                    typeof __EXPLORER_URL__ !== "undefined"
                      ? __EXPLORER_URL__
                      : "";
                  return (
                    <div
                      key={`${String(swap.timestamp)}-${String(i)}`}
                      className="flex cursor-pointer items-center justify-between rounded-sm px-1 py-1 text-[11px] text-white/50 transition-colors hover:bg-white/5"
                      onClick={() => {
                        setTokenIn(swap.tokenIn as TokenSymbol);
                        setTokenOut(swap.tokenOut as TokenSymbol);
                        setAmount(swap.amount);
                        if (PROTOCOLS.includes(swap.protocol as Protocol))
                          setProtocol(swap.protocol as Protocol);
                      }}
                      title="Click to repeat this swap"
                    >
                      <span className="flex items-center gap-1">
                        <span className="text-[var(--color-success)]">
                          {"\u2713"}
                        </span>
                        <span>
                          {formatDisplayAmount(swap.amount).display}{" "}
                          {swap.tokenIn} {"\u2192"}{" "}
                          {swap.amountOut
                            ? formatDisplayAmount(swap.amountOut).display
                            : ""}{" "}
                          {swap.tokenOut}
                        </span>
                        {swap.usdValue ? (
                          <span className="opacity-40">
                            {formatUsd(swap.usdValue)}
                          </span>
                        ) : null}
                        <span className="opacity-40">({swap.protocol})</span>
                      </span>
                      <div className="flex items-center gap-2">
                        {swap.hash && explorerUrl ? (
                          <a
                            href={`${explorerUrl}/tx/${swap.hash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono opacity-60 hover:text-[var(--color-gold)] hover:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            {truncateAddress(swap.hash)}
                          </a>
                        ) : swap.hash ? (
                          <code
                            className="cursor-pointer font-mono opacity-60 hover:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              void navigator.clipboard.writeText(
                                swap.hash as string,
                              );
                            }}
                            title="Click to copy tx hash"
                          >
                            {truncateAddress(swap.hash)}
                          </code>
                        ) : null}
                        <span className="opacity-40">
                          {formatSmartTime(swap.timestamp)}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {recentSwaps.length > 5 && (
                  <div className="text-[10px] text-white/30">
                    +{recentSwaps.length - 5} more
                  </div>
                )}
                {confirmClear ? (
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className="text-white/40">Clear all?</span>
                    <button
                      type="button"
                      onClick={() => {
                        setRecentSwaps([]);
                        setConfirmClear(false);
                      }}
                      className="text-red-400 transition-colors hover:text-red-300"
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setConfirmClear(false);
                      }}
                      className="text-white/40 transition-colors hover:text-white/60"
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setConfirmClear(true);
                    }}
                    className="text-[10px] text-white/30 transition-colors hover:text-white/50"
                  >
                    Clear
                  </button>
                )}
              </div>
            </CollapsibleSection>
          </div>
        )}

        {/* Swap button */}
        <div className="px-3 pb-3 pt-1">
          {!isConnected ? (
            <Alert variant="warning" title="Wallet Required">
              Connect a wallet using the bar above to execute swaps.
            </Alert>
          ) : (
            <Button
              variant={
                insufficientBalance
                  ? "outline"
                  : effectivePriceImpact !== null && effectivePriceImpact > 5
                    ? "outline"
                    : canSwap
                      ? "gold"
                      : "outline"
              }
              size="md"
              onClick={handleSwap}
              disabled={!isValid || !canSwap || flow.isExecuting}
              loading={flow.isExecuting}
              className={cn(
                "w-full",
                insufficientBalance && "!bg-red-900/20 !text-red-400",
                effectivePriceImpact !== null &&
                  effectivePriceImpact > 5 &&
                  !insufficientBalance &&
                  "!bg-red-900/20 !text-red-400",
              )}
              data-testid="swap-button"
            >
              {buttonLabel}
            </Button>
          )}
        </div>
      </PcbCard>

      {/* ---- Confirmation Modal ---- */}
      <Modal
        open={showConfirmation}
        onOpenChange={(open) => {
          setShowConfirmation(open);
          if (!open) {
            setHighImpactAcknowledged(false);
            setShowFullPrecision(false);
          }
        }}
        title="Confirm Swap"
        size="sm"
      >
        <div className="space-y-4 py-2">
          {/* High price impact warning banner */}
          {effectivePriceImpact !== null && effectivePriceImpact > 5 && (
            <Alert variant="error" title="High Price Impact" className="mx-0">
              This swap has a {effectivePriceImpact.toFixed(2)}% price impact.
            </Alert>
          )}

          {/* You pay */}
          <div className="octa-md flex items-center justify-between bg-[rgba(255,255,255,0.04)] p-4">
            <div>
              <div className="text-xs text-white/40">You pay</div>
              <div className="mt-1 font-mono text-lg font-medium text-white">
                {formatDisplayAmount(amount).display} {tokenIn}
              </div>
              {showFullPrecision && fullPrecisionAmount.current && (
                <div className="mt-0.5 font-mono text-[10px] text-white/30">
                  {fullPrecisionAmount.current}
                </div>
              )}
              <div className="font-mono text-[11px] text-white/40">
                <AnimatedBalance
                  value={
                    parseFloat(amount || "0") *
                    (DEVENV_TOKEN_PRICES[tokenIn] ?? 0)
                  }
                  format={formatUsd}
                  emDash={false}
                />
              </div>
              {fullPrecisionAmount.current && (
                <button
                  type="button"
                  onClick={() => {
                    setShowFullPrecision((v) => !v);
                  }}
                  className="hover:text-[var(--color-gold)]/80 mt-0.5 text-[9px] text-[var(--color-gold)] transition-colors"
                  data-testid="full-precision-toggle"
                >
                  {showFullPrecision
                    ? "Hide full precision"
                    : "Show full precision"}
                </button>
              )}
            </div>
            <TokenIcon symbol={tokenIn} size={32} />
          </div>

          {/* Arrow */}
          <div className="flex justify-center text-white/30">{"\u2193"}</div>

          {/* You receive */}
          <div className="octa-md flex items-center justify-between bg-[rgba(255,255,255,0.04)] p-4">
            <div>
              <div className="text-xs text-white/40">You receive</div>
              <div className="mt-1 font-mono text-lg font-medium text-white">
                {outputNumeric > 0
                  ? formatDisplayAmount(outputNumeric).display
                  : "\u2014"}{" "}
                {tokenOut}
              </div>
              <div className="font-mono text-[11px] text-white/40">
                ~
                <AnimatedBalance
                  value={outputNumeric * (DEVENV_TOKEN_PRICES[tokenOut] ?? 0)}
                  format={formatUsd}
                  emDash={false}
                />
              </div>
            </div>
            <TokenIcon symbol={tokenOut} size={32} />
          </div>

          {/* Details */}
          <div className="octa-md space-y-1.5 bg-[rgba(255,255,255,0.02)] p-4 text-xs">
            {exchangeRate && (
              <div className="flex items-center justify-between text-white/50">
                <span>Exchange rate</span>
                <span className="font-mono">
                  1 {tokenIn} ={" "}
                  <AnimatedBalance
                    value={exchangeRate.forward}
                    format={(n) => formatTokenDisplay(n).display}
                    emDash={false}
                  />{" "}
                  {tokenOut}
                </span>
              </div>
            )}
            {minimumReceived !== null && (
              <div className="flex items-center justify-between text-white/50">
                <span>Minimum received</span>
                <span
                  className={cn(
                    "font-mono",
                    effectivePriceImpact !== null &&
                      effectivePriceImpact > 3 &&
                      "text-amber-400",
                  )}
                >
                  {formatTokenDisplay(minimumReceived).display} {tokenOut}
                </span>
              </div>
            )}
            {effectivePriceImpact !== null && (
              <div className="flex items-center justify-between">
                <span className="text-white/50">Price impact</span>
                <span className={`font-mono ${priceImpactColor ?? ""}`}>
                  <AnimatedBalance
                    value={effectivePriceImpact}
                    format={(n) => n.toFixed(2)}
                    suffix="%"
                    emDash={false}
                  />
                </span>
              </div>
            )}
            <div className="flex items-center justify-between text-white/50">
              <span>Swap fee</span>
              <span className="font-mono">
                {feeSummary.tokenAmount > 0
                  ? formatFeeWithPercent(feeSummary.usdAmount, feeSummary.pct)
                  : feeSummary.pct}
              </span>
            </div>
            {gasCost && (
              <div className="flex items-center justify-between text-white/50">
                <span>Network fee</span>
                <span className="font-mono">
                  ~
                  <AnimatedBalance
                    value={gasCost.usdCost}
                    format={formatUsd}
                    emDash={false}
                  />
                </span>
              </div>
            )}
            {/* Total cost row */}
            {(feeSummary.usdAmount > 0 || gasCost) && (
              <div className="flex items-center justify-between pt-1.5 text-white/60">
                <span className="font-medium">Total cost</span>
                <span className="font-mono font-medium">
                  ~
                  <AnimatedBalance
                    value={feeSummary.usdAmount + (gasCost?.usdCost ?? 0)}
                    format={formatUsd}
                    emDash={false}
                  />
                </span>
              </div>
            )}
          </div>

          {/* High impact confirmation toggle */}
          {effectivePriceImpact !== null && effectivePriceImpact > 5 && (
            <button
              type="button"
              onClick={() => {
                setHighImpactAcknowledged((v) => !v);
              }}
              className="octa-sm flex w-full cursor-pointer items-center gap-3 bg-[rgba(239,68,68,0.06)] p-3 text-left text-[11px] text-red-400 transition-colors"
            >
              <span
                className={cn(
                  "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-200",
                  highImpactAcknowledged ? "bg-red-500/80" : "bg-white/10",
                )}
              >
                <span
                  className={cn(
                    "inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform duration-200",
                    highImpactAcknowledged
                      ? "translate-x-[18px]"
                      : "translate-x-[3px]",
                  )}
                />
              </span>
              I understand this swap has {effectivePriceImpact.toFixed(1)}%
              price impact
            </button>
          )}

          {/* Cancel + Confirm buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowConfirmation(false);
              }}
              className="flex-1"
              data-testid="cancel-swap-button"
            >
              Cancel
            </Button>
            <Button
              variant={
                effectivePriceImpact !== null && effectivePriceImpact > 5
                  ? "outline"
                  : "gold"
              }
              size="sm"
              onClick={executeSwap}
              loading={flow.isExecuting}
              disabled={
                flow.isExecuting ||
                (effectivePriceImpact !== null &&
                  effectivePriceImpact > 5 &&
                  !highImpactAcknowledged)
              }
              className={cn(
                "flex-1",
                effectivePriceImpact !== null &&
                  effectivePriceImpact > 5 &&
                  "!bg-red-900/20 !text-red-400",
              )}
              data-testid="confirm-swap-button"
            >
              {flow.isExecuting ? "Confirming..." : "Confirm Swap"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Recent swaps are now inline inside the main card */}
    </div>
  );
}
